class DotoVariantOptions extends HTMLElement {
  constructor() {
    super();
    this.track = null;
    this.dotsRoot = null;
    this.scrollHint = null;
    this.items = [];
    this.pages = [];
    this.currentPage = 0;
    this.isGrouped = false;
    this.scrollFrame = 0;
    this.mediaQuery = window.matchMedia('(max-width: 991px)');

    this.handleMediaChange = this.handleMediaChange.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.updateScrollHint = this.updateScrollHint.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector('[data-doto-variant-track]');
    this.dotsRoot = this.querySelector('[data-doto-variant-dots]');

    if (!this.track) return;

    this.items = Array.from(this.track.children).filter((item) => item.matches('label'));
    this.itemsPerPage = Number.parseInt(this.dataset.itemsPerPage || '4', 10) || 4;

    this.createScrollHint();

    this.mediaQuery.addEventListener?.('change', this.handleMediaChange);
    this.handleMediaChange();

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isGrouped) {
          // Never force scrollLeft from ResizeObserver. On iOS the browser chrome
          // changes the viewport while the page scrolls; writing scrollLeft here
          // can create the visible 'rebote' / snap-back effect.
          window.cancelAnimationFrame(this.scrollFrame);
          this.scrollFrame = window.requestAnimationFrame(this.updateScrollHint);
        }
      });
      this.resizeObserver.observe(this);
    }
  }

  disconnectedCallback() {
    this.mediaQuery.removeEventListener?.('change', this.handleMediaChange);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.scrollFrame);
  }

  createScrollHint() {
    if (this.scrollHint) return;

    const hint = document.createElement('span');
    hint.className = 'doto-variant-options__scroll-hint';
    hint.setAttribute('aria-hidden', 'true');
    this.appendChild(hint);
    this.scrollHint = hint;
  }

  handleMediaChange() {
    if (!this.track) return;

    if (this.mediaQuery.matches) {
      this.groupForMobile();
    } else {
      this.restoreDesktop();
    }
  }

  groupForMobile() {
    if (this.isGrouped || !this.items.length) {
      this.updateScrollHint();
      return;
    }

    this.track.classList.add('doto-variant-track--mobile');
    this.pages = [];

    for (let index = 0; index < this.items.length; index += this.itemsPerPage) {
      const page = document.createElement('div');
      page.className = 'doto-variant-options__page';
      page.dataset.pageIndex = String(this.pages.length);

      this.items.slice(index, index + this.itemsPerPage).forEach((item) => page.appendChild(item));
      this.track.appendChild(page);
      this.pages.push(page);
    }

    this.isGrouped = true;
    this.buildDots();
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });

    const selectedInput = this.querySelector('input[type="radio"]:checked');
    const selectedPage = selectedInput?.closest('.doto-variant-options__page');
    this.currentPage = selectedPage ? Number.parseInt(selectedPage.dataset.pageIndex || '0', 10) : 0;

    requestAnimationFrame(() => {
      const page = this.pages[this.currentPage];
      if (page) this.track.scrollLeft = page.offsetLeft;
      this.setCurrentPage(this.currentPage);
      this.updateScrollHint();
    });
  }

  restoreDesktop() {
    if (!this.track) return;

    if (this.isGrouped) {
      this.track.removeEventListener('scroll', this.handleScroll);
      this.items.forEach((item) => this.track.appendChild(item));
      this.pages.forEach((page) => page.remove());
      this.pages = [];
      this.isGrouped = false;
      this.currentPage = 0;
      this.track.classList.remove('doto-variant-track--mobile');

      if (this.dotsRoot) {
        this.dotsRoot.replaceChildren();
        this.dotsRoot.hidden = true;
      }
    }

    this.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
  }

  buildDots() {
    if (!this.dotsRoot) return;

    this.dotsRoot.replaceChildren();
    this.dotsRoot.hidden = this.pages.length <= 1;

    if (this.pages.length <= 1) return;

    this.pages.forEach((page, pageIndex) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'doto-variant-options__dot';
      dot.setAttribute(
        'aria-label',
        `Ver opciones ${pageIndex * this.itemsPerPage + 1} a ${Math.min((pageIndex + 1) * this.itemsPerPage, this.items.length)}`
      );
      dot.setAttribute('aria-current', pageIndex === this.currentPage ? 'true' : 'false');
      dot.addEventListener('click', () => this.scrollToPage(pageIndex, true));
      this.dotsRoot.appendChild(dot);
    });
  }

  handleScroll() {
    window.cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = window.requestAnimationFrame(() => {
      if (!this.pages.length) {
        this.updateScrollHint();
        return;
      }

      const scrollLeft = this.track.scrollLeft;
      let closestPage = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      this.pages.forEach((page, pageIndex) => {
        const distance = Math.abs(page.offsetLeft - scrollLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = pageIndex;
        }
      });

      this.setCurrentPage(closestPage);
      this.updateScrollHint();
    });
  }

  scrollToPage(pageIndex, smooth = true) {
    const page = this.pages[pageIndex];
    if (!page || !this.track) return;

    this.currentPage = pageIndex;
    this.track.scrollTo({
      left: page.offsetLeft,
      behavior: smooth && !window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'auto'
    });
    this.setCurrentPage(pageIndex);

    requestAnimationFrame(this.updateScrollHint);
    window.setTimeout(this.updateScrollHint, smooth ? 260 : 0);
  }

  setCurrentPage(pageIndex) {
    this.currentPage = pageIndex;
    Array.from(this.dotsRoot?.children || []).forEach((dot, index) => {
      dot.setAttribute('aria-current', index === pageIndex ? 'true' : 'false');
    });
  }

  updateScrollHint() {
    if (!this.track || !this.mediaQuery.matches || !this.isGrouped) {
      this.classList.remove('is-scrollable', 'is-at-start', 'is-at-end');
      return;
    }

    const maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    const scrollable = this.pages.length > 1 && maxScroll > 4;
    const atStart = this.track.scrollLeft <= 4;
    const atEnd = this.track.scrollLeft >= maxScroll - 4;

    this.classList.toggle('is-scrollable', scrollable);
    this.classList.toggle('is-at-start', atStart);
    this.classList.toggle('is-at-end', atEnd);

    const hintTop = this.track.offsetTop + (this.track.clientHeight / 2);
    this.style.setProperty('--doto-variant-hint-top', `${Math.round(hintTop)}px`);
  }
}

if (!customElements.get('doto-variant-options')) {
  customElements.define('doto-variant-options', DotoVariantOptions);
}
